// Copyright ©️ 2026 eVolve MEP, LLC

import type { AutodeskDbId } from 'components/Autodesk/types';

import { type SelectionMouseEvent, SINGLE_SELECT_TOOL_NAME } from './types';

const { Autodesk } = window;

class SingleSelectTool implements Autodesk.Viewing.ToolInterface {
  active: boolean;
  isDragging: boolean;
  viewer: Autodesk.Viewing.GuiViewer3D;

  constructor(viewer: Autodesk.Viewing.GuiViewer3D) {
    this.viewer = viewer;

    this.active = false;
    this.isDragging = false;
  }

  /* oxlint-disable no-empty-function */
  register() {}

  /* oxlint-disable no-empty-function */
  deregister() {}

  activate() {
    this.active = true;
  }

  deactivate() {
    this.active = false;
  }

  getName() {
    return SINGLE_SELECT_TOOL_NAME;
  }

  getNames() {
    return [SINGLE_SELECT_TOOL_NAME];
  }

  getCursor() {
    return 'default';
  }

  update(_timestamp: number) {
    return false;
  }

  // Tools get called in the order of their priority (higher numbers first). Default tools have a priority of 0.
  getPriority() {
    return 97;
  }

  handleSingleClick(event: SelectionMouseEvent, button: number) {
    const isLeftClick = button === 0;
    if (!isLeftClick) return false;

    // Find the element clicked that has been clicked. The result will be null if empty space has been clicked.
    const castResult: { dbId?: unknown } | null = this.viewer.clientToWorld(event.canvasX, event.canvasY);
    const selectedElementId: number | null = typeof castResult?.dbId === 'number' ? castResult.dbId : null;

    // Do nothing if the user clicks empty space.
    if (selectedElementId === null) return true;

    const existingSelection: AutodeskDbId[] = this.viewer.getSelection();

    const hasMultipleSelectedElements = existingSelection.length > 1;

    // If there are multiple selected elements, the new selection should replace them all (even if the clicked element is already selected).
    if (hasMultipleSelectedElements) {
      this.viewer.select([selectedElementId]);
      return true;
    }

    // If the element is already selected, de-select it.
    if (existingSelection.includes(selectedElementId)) {
      this.viewer.clearSelection();
      return true;
    }

    // Otherwise, select the clicked element only.
    this.viewer.select([selectedElementId]);
    return true;
  }
}

class SingleSelectExtension extends Autodesk.Viewing.Extension {
  tool: SingleSelectTool;

  constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: Autodesk.Viewing.ExtensionOptions) {
    super(viewer, options);
    this.tool = new SingleSelectTool(viewer);
  }

  load() {
    this.viewer.toolController.registerTool(this.tool);

    return true;
  }

  unload() {
    this.viewer.toolController.deactivateTool(SINGLE_SELECT_TOOL_NAME);
    this.viewer.toolController.deregisterTool(this.tool);

    return true;
  }
}

Autodesk.Viewing.theExtensionManager.registerExtension('SingleSelectExtension', SingleSelectExtension);
